import { Spacing } from '@persona-id/design-tokens';
import type { Location } from 'history';
import { debounce } from 'lodash';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { matchPath } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import { TOOLBAR_TRANSITION } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import DynamicInput from 'dashboard/components/DynamicInput';
import InquiryFlowPreview from 'dashboard/components/InquiryFlowPreview';
import LocalizedDirection from 'dashboard/components/LocalizedDirection';
import NavBack from 'dashboard/components/NavBack';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import { Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import { mergeResourceWithFormValues } from 'dashboard/lib/utils/formData';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { ThemeSet, ThemeSetAttributes, ThemeSetStyleMap } from 'dashboard/models/theme-set';
import { getLatestVersionId } from 'dashboard/routes/DashboardThemeSetsEditorRoute/DashboardThemeSetsEditorRoute';
import { THEME_SET_FORM_COMPONENT_CATEGORIES } from 'dashboard/views/DashboardThemeSetsEditorNewView/theme-set-editor-form-categories';
import ThemeSetEditorForm from 'dashboard/views/DashboardThemeSetsEditorNewView/ThemeSetEditorForm';
import { findPreviewStepsIndexToUpdate } from 'dashboard/views/DashboardThemeSetsEditorNewView/util';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import type { StepPreview } from 'lib/interfaces/StepsConfig';
import { useFormSecurely } from 'lib/security/reactHookForm';

const THEME_SET_EDITOR_ROOT_PATH = '/dashboard/theme-sets/:themeSetId';

const ConfigSection = styled('div')`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  flex: 0 0 400px;
  transition:
    flex ${TOOLBAR_TRANSITION},
    transform ${TOOLBAR_TRANSITION};
  padding: 0px;
`;

const PreviewContainer = styled('div')`
  height: calc(100vh - ${FULL_SCREEN_HEADER_HEIGHT}px);
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: row;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
`;

interface DashboardThemeSetsEditorViewNewProps {
  inquiryTemplates: InquiryTemplate[];
  inquiryTemplateVersion: InquiryTemplateVersion;
  themeSet: ThemeSet;
  stepsPreview: StepPreview[];
  locale: InquiryIso6391LanguageCode;
  styleMap: ThemeSetStyleMap;
  handleUpdateThemeSet: (
    themeSet: ThemeSet,
    themeSetAttributes: Partial<ThemeSetAttributes>,
  ) => Promise<void>;
  handleInquiryTemplateChange: (inquiryTemplateVersionId: string) => Promise<void>;
  handleLocaleChange: (locale: InquiryIso6391LanguageCode) => Promise<void>;
  handlePreviewChange: (
    inquiryTemplateVersion: InquiryTemplateVersion,
    themeSet: ThemeSet,
    startPaginationIndex: number,
    endPaginationIndex: number,
  ) => Promise<void>;
}

const DashboardThemeSetsEditorNewView = ({
  inquiryTemplates,
  inquiryTemplateVersion,
  themeSet,
  stepsPreview,
  locale,
  handleUpdateThemeSet,
  handleInquiryTemplateChange,
  handleLocaleChange,
  handlePreviewChange,
}: DashboardThemeSetsEditorViewNewProps) => {
  const [slideNumber, setSlideNumber] = useState(0);
  const [themeSetLiveEdited, setThemeSetLiveEdited] = useState(themeSet);

  const {
    control,
    formState: { isDirty: hasUnsavedChanges, isSubmitting },
    handleSubmit,
    reset,
    watch,
  } = useFormSecurely<ThemeSetAttributes>({
    values: themeSet.attributes,
  });
  const pages = useMemo<React.ReactElement[]>(
    () => Object.values(generatePages(stepsPreview)).flatMap((pages) => pages),
    [stepsPreview],
  );

  useEffect(() => {
    const [startIndex, endIndex] = findPreviewStepsIndexToUpdate(stepsPreview, pages, slideNumber);

    handlePreviewChange(inquiryTemplateVersion, themeSetLiveEdited, startIndex, endIndex);
  }, [inquiryTemplateVersion, slideNumber, themeSetLiveEdited]);

  // Update `themeSetLiveEdited` whenever the form values change
  useEffect(() => {
    // Debounced to lower the number of times the new style map gets fetched via `handleThemeAttributesChange`
    const debouncedUpdateThemeSetLiveEdited = debounce(
      (updatedThemeSetAttributes: ThemeSetAttributes) => {
        const updatedThemeSet = mergeResourceWithFormValues<ThemeSet>(
          themeSet,
          // Note (Fawn): This casting is needed as otherwise `formValues` will be a `DeepPartial<ThemeSetAttributes>`.
          // However, this callback in `watch` should always receive a full `ThemeSetAttributes` object anyways.
          // https://github.com/react-hook-form/react-hook-form/issues/9919
          updatedThemeSetAttributes,
        );
        setThemeSetLiveEdited(updatedThemeSet);
      },
      30,
    );

    const { unsubscribe } = watch((formValues) => {
      // NOTE (Fawn): This casting is needed as otherwise `formValues` will be a `DeepPartial<ThemeSetAttributes>`.
      // However, this callback in `watch` should always receive a full `ThemeSetAttributes` object anyways.
      // https://github.com/react-hook-form/react-hook-form/issues/9919
      debouncedUpdateThemeSetLiveEdited(formValues as ThemeSetAttributes);
    });
    return () => unsubscribe();
  }, [themeSet, watch]);

  const updateThemeSet = async (themeSetAttributes: ThemeSetAttributes) => {
    const updatedThemeSet = mergeResourceWithFormValues<ThemeSet>(themeSet, themeSetAttributes);
    await handleUpdateThemeSet(updatedThemeSet, {
      name: themeSetAttributes.name,
      'light-base-theme': themeSetAttributes['light-base-theme'],
      'light-inquiry-theme': themeSetAttributes['light-inquiry-theme'],
    });
  };

  const settings = {
    arrows: false,
    centerMode: true,
    dots: true,
    focusOnSelect: true,
    infinite: false,
    speed: 0,
    swipeToSlide: false,
    variableWidth: true,
    initialSlide: slideNumber,
    afterChange: (index: number) => {
      setSlideNumber(index);
    },
  };

  // Only warn the user about unsaved changes if the user is navigating away from the theme set editor as a whole
  // (ignores when navigating between sub-routes of the theme set editor)
  const blockOnRouteChange = useCallback(
    (prevLocation: Location, nextLocation?: Location) => {
      const prevIsTSE = matchPath(
        { path: THEME_SET_EDITOR_ROOT_PATH, end: false },
        prevLocation.pathname,
      );
      const nextIsTSE = matchPath(
        { path: THEME_SET_EDITOR_ROOT_PATH, end: false },
        nextLocation?.pathname ?? '',
      );
      return !(prevIsTSE && nextIsTSE) && hasUnsavedChanges;
    },
    [hasUnsavedChanges],
  );

  return (
    <>
      <NavConfirmPrompt prevent={blockOnRouteChange} />
      <Header
        prepend={<NavBack to='/dashboard/theme-sets' aria-label='Go back to themes' />}
        title={
          <HorizontalContainer>
            <Controller
              name='name'
              control={control}
              render={({ field: { value, onChange } }) => (
                <DynamicInput
                  aria-label="Edit Theme set's name"
                  value={value}
                  onChange={onChange}
                />
              )}
            />
          </HorizontalContainer>
        }
      >
        <HStack spacing={Spacing[3]}>
          <Button
            type='button'
            variant='basic'
            id='theme-set-editor__undo'
            disabled={isSubmitting || !hasUnsavedChanges}
            onClick={() => reset()}
          >
            Undo all
          </Button>
          <Button
            type='submit'
            id='theme-set-editor__save'
            disabled={isSubmitting || !hasUnsavedChanges}
            isPending={isSubmitting}
            onClick={handleSubmit(updateThemeSet)}
          >
            Save
          </Button>
        </HStack>
      </Header>
      <Suspense fallback={null}>
        <LocalizedDirection>
          <ContentContainer>
            <ConfigSection>
              <ThemeSetEditorForm
                categoriesSchema={THEME_SET_FORM_COMPONENT_CATEGORIES}
                control={control}
                disabled={isSubmitting}
                themeSet={themeSet}
                inquiryTemplates={inquiryTemplates}
                inquiryTemplateVersion={inquiryTemplateVersion}
                handleInquiryTemplateChange={async (templateId: string) => {
                  setSlideNumber(0);
                  await handleInquiryTemplateChange(
                    getLatestVersionId(
                      inquiryTemplates.find((it) => it.id === templateId) as InquiryTemplate,
                    ) as string,
                  );
                }}
                locale={locale}
                handleLocaleChange={handleLocaleChange}
                reactRouterV6={true}
              />
            </ConfigSection>
            <PreviewContainer>
              <InquiryFlowPreview
                theme={null}
                themeSet={themeSetLiveEdited}
                templateId={inquiryTemplateVersion?.id}
                pages={pages}
                settings={settings}
                showProgressBar={false}
              />
            </PreviewContainer>
          </ContentContainer>
        </LocalizedDirection>
      </Suspense>
    </>
  );
};

export default DashboardThemeSetsEditorNewView;
